import React, {useCallback, useEffect, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';

import {
    FontVariant,
    Button,
    CenterLayout,
    ThemeProvider,
    Text,
    ConfirmationModal,
    Spinner,
} from '@pexip/components';

import {SettingsPanel} from '../SettingsPanel/SettingsPanel.view';
import {VideoLayoutOptionValues} from '../../types';
import {
    ImgLayoutThumbnail,
    LayoutThumbnail,
    Thumbnail,
    NOT_SUPPORTED_PERSONAL_LAYOUTS,
} from '../LayoutThumbnail/LayoutThumbnail.view';
import {TestId} from '../../../test/testIds';

import styles from './MeetingLayout.module.scss';

const LAYOUT_MAPPING = new Map<string, string[]>([
    [
        VideoLayoutOptionValues.AdaptiveComposition,
        [
            '5:7',
            'ac_presentation_in_mix',
            'ac_presentation_in_mix_group',
            'ac_extended',
        ],
    ],
    [VideoLayoutOptionValues.Equal22, ['4:0']],
    [VideoLayoutOptionValues.Equal33, ['9:0']],
    [VideoLayoutOptionValues.Equal44, ['16:0']],
    [VideoLayoutOptionValues.Equal55, ['25:0']],
]);

const isActive = (layout: string, layoutSelection: string | undefined) => {
    if (layoutSelection === layout) {
        return true;
    }

    // FIXME: workaround because of #35453
    return Boolean(
        layoutSelection &&
        LAYOUT_MAPPING.get(layout)?.includes(layoutSelection),
    );
};

export const MeetingLayoutLoading: React.FC = () => (
    <SettingsPanel fillHeight={false} withScrollbars={false}>
        <CenterLayout className={styles.layoutLoadingWrapper}>
            <Spinner colorScheme="dark" />
        </CenterLayout>
    </SettingsPanel>
);

export const MeetingLayout: React.FC<{
    currentLayout?: string;
    currentPersonalLayout?: string;
    close: (e: React.SyntheticEvent<HTMLElement>) => void;
    setLayout: (
        layout: string | undefined,
        onDone?: () => void,
        onFail?: (error: unknown) => void,
    ) => void;
    setPersonalLayout: (
        layout: string | undefined,
        onDone?: () => void,
        onFail?: (error: unknown) => void,
    ) => void;
    resetToMeetingLayout: (
        onDone?: () => void,
        onFail?: (error: unknown) => void,
    ) => void;
    isConfirmationOpen: boolean;
    openConfirmation: () => void;
    closeConfirmation: () => void;
    layouts: Map<string, {primary: string; active: string}>;
    canChangeLayout?: boolean;
    canChangePersonalLayout?: boolean;
}> = ({
    currentLayout,
    currentPersonalLayout,
    setLayout,
    setPersonalLayout,
    resetToMeetingLayout,
    canChangeLayout,
    canChangePersonalLayout,
    isConfirmationOpen,
    openConfirmation,
    closeConfirmation,
    layouts,
    close,
}) => {
    const {t} = useTranslation();
    const [isSaving, setIsSaving] = useState(false);
    const [layoutSelection, setLayoutSelection] = useState<string | undefined>(
        currentPersonalLayout ?? currentLayout,
    );

    const handleSave = useCallback(
        (e: React.SyntheticEvent<HTMLElement>) => {
            setIsSaving(true);
            if (layoutSelection) {
                const onDone = () => {
                    if (currentPersonalLayout) {
                        resetToMeetingLayout(
                            () => {
                                setIsSaving(false);
                                close(e);
                            },
                            () => {
                                setIsSaving(false);
                            },
                        );
                    } else {
                        setIsSaving(false);
                        close(e);
                    }
                };
                setLayout(layoutSelection, onDone, () => {
                    setIsSaving(false);
                });
            }
            closeConfirmation();
        },
        [
            layoutSelection,
            closeConfirmation,
            setLayout,
            close,
            currentPersonalLayout,
            resetToMeetingLayout,
        ],
    );

    const handlePersonalSave = useCallback(
        (e: React.SyntheticEvent<HTMLElement>) => {
            setIsSaving(true);
            if (layoutSelection) {
                setPersonalLayout(
                    layoutSelection,
                    () => {
                        setIsSaving(false);
                        close(e);
                    },
                    () => setIsSaving(false),
                );
            }
        },
        [layoutSelection, setPersonalLayout, close],
    );

    const handleResetToMeetingLayout = useCallback(
        (e: React.SyntheticEvent<HTMLElement>) => {
            setIsSaving(true);
            resetToMeetingLayout(
                () => {
                    setIsSaving(false);
                    close(e);
                },
                () => setIsSaving(false),
            );
        },
        [resetToMeetingLayout, close],
    );

    const getLayoutThumbnail = (layout: string) => {
        return (
            <LayoutThumbnail
                active={isActive(layout, layoutSelection)}
                canChangePersonalLayout={canChangePersonalLayout}
                className={styles.layoutOption}
                layout={layout}
                key={layout}
                selectLayout={setLayoutSelection}
            />
        );
    };

    const getDefaultLayouts = () =>
        [
            VideoLayoutOptionValues.AdaptiveComposition,
            VideoLayoutOptionValues.Speaker7,
            VideoLayoutOptionValues.Speaker21,
            VideoLayoutOptionValues.Speaker221,
            VideoLayoutOptionValues.Speaker33,
            VideoLayoutOptionValues.Highlight,
            VideoLayoutOptionValues.Equal22,
            VideoLayoutOptionValues.Equal33,
            VideoLayoutOptionValues.Equal44,
            VideoLayoutOptionValues.Equal55,
        ].map(getLayoutThumbnail);

    const getLayouts = () =>
        Array.from(layouts.entries()).flatMap(([layout, svg]) => {
            // Remove AC layout if you are a guest
            if (
                !canChangeLayout &&
                NOT_SUPPORTED_PERSONAL_LAYOUTS.includes(layout)
            ) {
                return [];
            }

            return svg.primary && svg.active
                ? [
                      <Thumbnail
                          key={layout}
                          layout={layout}
                          canChangePersonalLayout={canChangePersonalLayout}
                          selectLayout={setLayoutSelection}
                          active={isActive(layout, layoutSelection)}
                      >
                          <ImgLayoutThumbnail
                              name={layout}
                              svg={svg}
                              active={isActive(layout, layoutSelection)}
                          />
                      </Thumbnail>,
                  ]
                : [getLayoutThumbnail(layout)];
        });

    useEffect(() => {
        setLayoutSelection(currentPersonalLayout ?? currentLayout);
    }, [currentLayout, currentPersonalLayout]);

    const canChangeToSelectedPersonalLayout =
        canChangePersonalLayout &&
        !(
            NOT_SUPPORTED_PERSONAL_LAYOUTS.includes(layoutSelection ?? '') ||
            NOT_SUPPORTED_PERSONAL_LAYOUTS.includes(currentLayout ?? '')
        );

    return (
        <>
            <SettingsPanel
                footerContent={
                    <ThemeProvider colorScheme="light">
                        {canChangeToSelectedPersonalLayout && (
                            <Button
                                onClick={handlePersonalSave}
                                type="submit"
                                modifier="fullWidth"
                                className="mb-2"
                                isLoading={isSaving}
                                isDisabled={isSaving}
                                data-testid={TestId.ButtonSettingsSaveMyself}
                            >
                                <Trans t={t} i18nKey="settings.save-for-myself">
                                    Save for myself
                                </Trans>
                            </Button>
                        )}
                        {canChangeLayout && (
                            <Button
                                onClick={() => {
                                    openConfirmation();
                                }}
                                variant="secondary"
                                size="medium"
                                modifier="fullWidth"
                                isLoading={isSaving}
                                isDisabled={isSaving}
                                data-testid={TestId.ButtonSettingsSaveAll}
                            >
                                <Trans
                                    t={t}
                                    i18nKey="settings.save-for-everyone"
                                >
                                    Save for everyone
                                </Trans>
                            </Button>
                        )}
                    </ThemeProvider>
                }
            >
                <ThemeProvider colorScheme="light">
                    {layouts.size > 0 ? getLayouts() : getDefaultLayouts()}
                    {canChangePersonalLayout && currentPersonalLayout && (
                        <>
                            <Button
                                onClick={handleResetToMeetingLayout}
                                className="mt-4 mb-1"
                                variant="tertiary"
                                size="medium"
                                modifier="fullWidth"
                                data-testid={
                                    TestId.ButtonSettingsResetToMeetingLayout
                                }
                            >
                                <Trans
                                    t={t}
                                    i18nKey="settings.reset-to-meeting-layout"
                                >
                                    Reset to meeting layout
                                </Trans>
                            </Button>
                            <Text fontVariant={FontVariant.Small}>
                                <Trans
                                    t={t}
                                    i18nKey="settings.layout-reset-to-meeting-layout-info"
                                >
                                    Resets your layout to the one chosen by host
                                    for this meeting
                                </Trans>
                            </Text>
                        </>
                    )}
                </ThemeProvider>
            </SettingsPanel>
            <ConfirmationModal
                closeOnOutsideClick={false}
                cancelButtonTestid={TestId.ConfirmationModalCancelButton}
                cancelButtonText={t('common.cancel', 'Cancel')}
                confirmButtonTestid={TestId.ConfirmationModalConfirmButton}
                confirmButtonText={t('common.change', 'Change')}
                data-testid={TestId.ModalLayoutChangeConfirmation}
                description={t(
                    'settings.layout-confirmation-desc',
                    'You’re about to change the layout for everyone in the meeting, not just yourself.',
                )}
                isOpen={isConfirmationOpen}
                onCancel={closeConfirmation}
                onClose={closeConfirmation}
                onConfirm={handleSave}
                title={t(
                    'settings.layout-confirmation-title',
                    'Changing layouts will affect everyone',
                )}
                uniqueTitle={t(
                    'settings.layout-confirmation-title',
                    'Changing layouts will affect everyone',
                )}
            />
        </>
    );
};
